// @flow

import React, { type Node, type StatelessFunctionalComponent } from "react";

import { compose, lifecycle, type HOC } from "recompose";

import { CSSTransition } from "react-transition-group";

import {
  FaInfoCircle,
  FaRegCheckCircle,
  FaRegTimesCircle,
} from "react-icons/fa";
import { MdClear } from "react-icons/md";

import Portal from "../portal";

import { withNotification } from "./hocs";
import { type NotificationId, type Replacements } from "./state";

import "./notification.css";
import classNames from "classnames";

type NotificationChildProps = {
  open: Function,
  close: Function,
  replacements: Replacements,
};

type Props = {
  id: NotificationId,
  replacements?: Replacements,
  children: NotificationChildProps => Node,
};

type EnhancedProps = {
  close: Function,
  open: boolean,
  show: boolean,
};

const enhance: HOC<Props & EnhancedProps, Props> = compose(
  withNotification,

  lifecycle({
    componentDidUpdate(prevProps) {
      if (prevProps.show !== this.props.show && this.props.show === true) {
        setTimeout(this.props.close, 3000);
      }
    },
  })
);

export const Notification = enhance(
  ({ children, close, open, replacements = {}, show }) => (
    <Portal>
      <CSSTransition
        in={show}
        timeout={600}
        classNames={{
          enter: "q-Notification--enter",
          enterActive: "q-Notification--enter-active",
          exit: "q-Notification--exit",
          exitActive: "q-Notification--exit-active",
        }}
        unmountOnExit
      >
        <div className="q-Notification">
          {children({ open, close, replacements })}
        </div>
      </CSSTransition>
    </Portal>
  )
);

type NotificationsProps = {
  id: NotificationId,
  children: Replacements => Node,
  type: "success" | "failure" | "info",
};

export const StyledNotification: StatelessFunctionalComponent<NotificationsProps> = ({
  children,
  id,
  type,
}) => {
  const types = {
    success: "q-SuccessNotification",
    failure: "q-FailureNotification",
    info: "q-InfoNotification",
  };

  const Icon = {
    success: <FaRegCheckCircle className="q-Notification__icon" />,
    failure: <FaRegTimesCircle className="q-Notification__icon" />,
    info: <FaInfoCircle className="q-Notification__icon" />,
  }[type];

  return (
    <Notification id={id}>
      {({ close: onClose, replacements }) => (
        <div className={classNames("q-Notification", types[type])}>
          {Icon}

          <div className="q-Notification__message">
            {children(replacements)}
          </div>

          <div className="q-Notification__close" onClick={onClose}>
            <MdClear />
          </div>
        </div>
      )}
    </Notification>
  );
};

type StyledNotificationsProps = {
  id: NotificationId,
  children: Replacements => Node,
};

export const SuccessNotification: StatelessFunctionalComponent<StyledNotificationsProps> = props => (
  <StyledNotification {...props} type="success" />
);

export const FailureNotification: StatelessFunctionalComponent<StyledNotificationsProps> = props => (
  <StyledNotification {...props} type="failure" />
);

export const InfoNotification: StatelessFunctionalComponent<StyledNotificationsProps> = props => (
  <StyledNotification {...props} type="info" />
);
